<template>
  <div class="order-confirmation-page">
    <div class="container">
      <div v-if="loading" class="loading">
        Loading order details...
      </div>

      <div v-else-if="error" class="error-state">
        <div class="error-icon">
          ⚠️
        </div>
        <h2>
          {{ error }}
        </h2>
        <button class="btn-home" @click="$router.push('/')">
          Go to Home
        </button>
      </div>

      <div v-else-if="order" class="confirmation-content">
        <div class="success-header">
          <div class="success-icon">
            ✓
          </div>
          <h1>
            Order Confirmed!
          </h1>
          <p>Thank you for your purchase. Your order has been received and is being processed.</p>
        </div>

        <div class="order-details-card">
          <div class="order-header">
            <div class="order-info">
              <h2>
                Order #{{ order.order_number }}
              </h2>
              <p class="order-date">
                {{ formatDate(order.created_at) }}
              </p>
            </div>
            <div class="order-status">
              <span :class="['status-badge', order.status]">
                {{ formatStatus(order.status) }}
              </span>
            </div>
          </div>

          <div class="section-divider"></div>

          <div class="order-items-section">
            <h3>Order Items</h3>
            <div class="order-items">
              <div
                v-for="item in order.items"
                :key="item.id"
                class="order-item"
              >
                <div class="item-image">
                  <img
                    v-if="item.product && item.product.images && item.product.images.length > 0"
                    :src="getPrimaryImage(item.product).image_url"
                    :alt="item.product_name"
                  />
                  <div v-else class="no-image">
                    No Image
                  </div>
                </div>

                <div class="item-details">
                  <div class="item-name">
                    {{ item.product_name }}
                  </div>
                  <div class="item-variants">
                    <span v-if="item.color">{{ item.color }}</span>
                    <span v-if="item.size">{{ item.size }}</span>
                  </div>
                </div>

                <div class="item-quantity">
                  Qty: {{ item.quantity }}
                </div>

                <div class="item-price">
                  ${{ formatPrice(item.unit_price_cents) }}
                </div>
              </div>
            </div>
          </div>

          <div class="section-divider"></div>

          <div class="shipping-info">
            <h3>Shipping Address</h3>
            <p class="shipping-address">
              {{ order.shipping_name }}<br>
              {{ order.shipping_address1 }}<br>
              <span v-if="order.shipping_address2">{{ order.shipping_address2 }}<br></span>
              {{ order.shipping_city }}, {{ order.shipping_state }} {{ order.shipping_postal_code }}<br>
              {{ order.shipping_country }}
            </p>
            <p class="shipping-email">
              Email: {{ order.shipping_email }}
            </p>
          </div>

          <div class="section-divider"></div>

          <div class="order-summary">
            <div class="summary-row">
              <span>Subtotal</span>
              <span>${{ formatPrice(order.total_cents) }}</span>
            </div>
            <div class="summary-row">
              <span>Shipping</span>
              <span class="free">FREE</span>
            </div>
            <div class="summary-row total">
              <span>Total</span>
              <span>${{ formatPrice(order.total_cents) }}</span>
            </div>
          </div>
        </div>

        <div class="action-buttons">
          <button class="btn-orders" @click="$router.push('/my-orders')">
            View All Orders
          </button>
          <button class="btn-shop" @click="$router.push('/shop')">
            Continue Shopping
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ProductRepository from '../data/product.repository'

export default {
  name: 'OrderConfirmation',
  data () {
    return {
      order: null,
      loading: false,
      error: null
    }
  },
  mounted () {
    this.loadOrder()
  },
  methods: {
    loadOrder () {
      this.loading = true
      this.error = null

      const orderId = this.$route.params.orderId

      ProductRepository.getOrder(orderId)
        .then((order) => {
          this.order = order
          this.loading = false
        })
        .catch((err) => {
          this.loading = false
          this.error = 'Order not found'
          console.error('Error loading order:', err)
        })
    },
    getPrimaryImage (product) {
      if (!product || !product.images || product.images.length === 0) return null
      return product.images.find(img => img.is_primary === 1) || product.images[0]
    },
    formatPrice (cents) {
      return (cents / 100).toFixed(2)
    },
    formatDate (dateString) {
      const date = new Date(dateString)
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    },
    formatStatus (status) {
      return status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ')
    }
  }
}
</script>

<style scoped>
.order-confirmation-page {
  padding: 20px 0;
  min-height: 60vh;
}

.container {
  max-width: 900px;
  margin: 0 auto;
  padding: 0 20px;
}

.loading {
  text-align: center;
  padding: 60px 20px;
  font-size: 18px;
  color: #718096;
}

.error-state {
  text-align: center;
  padding: 60px 20px;
}

.error-icon {
  font-size: 60px;
  margin-bottom: 20px;
}

.error-state h2 {
  font-size: 24px;
  color: #e53e3e;
  margin-bottom: 24px;
}

.btn-home {
  background: #3182ce;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 4px;
  font-size: 16px;
  cursor: pointer;
}

.success-header {
  text-align: center;
  margin-bottom: 30px;
}

.success-icon {
  width: 80px;
  height: 80px;
  background: #48bb78;
  color: white;
  font-size: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
}

.success-header h1 {
  font-size: 32px;
  margin: 0 0 12px 0;
  color: #2d3748;
}

.success-header p {
  font-size: 16px;
  color: #718096;
}

.order-details-card {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 24px;
}

.order-header {
  display: flex;
  justify-content: space-between;
  align-items: start;
}

.order-info h2 {
  font-size: 24px;
  margin: 0 0 8px 0;
  color: #2d3748;
}

.order-date {
  color: #718096;
  margin: 0;
}

.status-badge {
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 600;
}

.status-badge.paid {
  background: #d4edda;
  color: #155724;
}

.status-badge.pending {
  background: #fff3cd;
  color: #856404;
}

.section-divider {
  height: 1px;
  background: #e2e8f0;
  margin: 24px 0;
}

.order-items-section h3,
.shipping-info h3 {
  font-size: 18px;
  margin: 0 0 16px 0;
  color: #2d3748;
}

.order-items {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.order-item {
  display: grid;
  grid-template-columns: 80px 1fr auto auto;
  gap: 16px;
  align-items: center;
}

.item-image {
  width: 80px;
  height: 80px;
  background: #f7fafc;
  border-radius: 4px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.item-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.no-image {
  color: #a0aec0;
  font-size: 14px;
}

.item-details {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.item-name {
  font-weight: 600;
  color: #2d3748;
}

.item-variants {
  font-size: 14px;
  color: #718096;
}

.item-variants span:not(:last-child)::after {
  content: ' • ';
}

.item-quantity {
  color: #718096;
  font-size: 14px;
}

.item-price {
  font-weight: 600;
  color: #2d3748;
}

.shipping-address {
  line-height: 1.6;
  color: #4a5568;
  margin: 0 0 8px 0;
}

.shipping-email {
  color: #4a5568;
  margin: 0;
}

.order-summary {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  font-size: 16px;
  color: #4a5568;
}

.summary-row.total {
  font-size: 20px;
  font-weight: bold;
  color: #2d3748;
  padding-top: 12px;
  border-top: 2px solid #e2e8f0;
}

.summary-row .free {
  color: #48bb78;
  font-weight: 600;
}

.action-buttons {
  display: flex;
  gap: 12px;
  justify-content: center;
}

.btn-orders,
.btn-shop {
  padding: 12px 24px;
  border: none;
  border-radius: 4px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-orders {
  background: #3182ce;
  color: white;
}

.btn-orders:hover {
  background: #2c5aa0;
}

.btn-shop {
  background: #e2e8f0;
  color: #2d3748;
}

.btn-shop:hover {
  background: #cbd5e0;
}

@media (max-width: 768px) {
  .order-item {
    grid-template-columns: 60px 1fr;
    gap: 12px;
  }

  .item-quantity,
  .item-price {
    grid-column: 2;
  }
}
</style>



// WEBPACK FOOTER //
// OrderConfirmation.vue?9e1aea66